import { useMemo } from "react";
import {
  MEMBERS_TABLE_COLUMN_LABELS,
  MembersTableColumnField,
  MembersTableGridColDef,
} from "@pages/workspace-profile/tab-contents/workspace-members-tab/workspace-members-table-types";
import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  MemberAuthenticationTypeCell,
  MemberEmailCell,
  MemberNameCell,
  MemberOptionCell,
  MemberRoleInWorkspaceCell,
  MemberStatusCell,
} from "@utils/data-grid-utils/members-data-grid-cells";
import { BaseCompanyProps } from "@custom-types/base-types";
import { getMemberDisplayName } from "@utils/data-display";
import { UtilsTypes } from "@stellar/utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

const columnDimension: ColumnDimension<MembersTableColumnField> = {
  name: {
    flex: 1.5,
  },
  email: {
    flex: 1.5,
  },
  role: {
    flex: 1,
  },
  status: {
    flex: 1.5,
  },
  authenticationType: {
    flex: 1,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the workspace's members table
 */
export function useWorkspaceMembersTableColumns({
  company,
}: BaseCompanyProps): MembersTableGridColDef[] {
  const columnsMap: Record<MembersTableColumnField, MembersTableGridColDef> =
    useMemo(() => {
      return {
        name: {
          field: "name",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.name,
          ...columnDimension.name,
          valueGetter: (_, row) => getMemberDisplayName(row.entity),
          renderCell: MemberNameCell,
        },

        email: {
          field: "email",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.email,
          ...columnDimension.email,
          valueGetter: (_, row) => row.entity.email,
          renderCell: MemberEmailCell,
        },

        role: {
          field: "role",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.role,
          ...columnDimension.role,
          getApplyQuickFilterFn: () => null,
          valueGetter: (_, row) => row.entity.role,
          renderCell: MemberRoleInWorkspaceCell,
        },

        status: {
          field: "status",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.status,
          ...columnDimension.status,
          getApplyQuickFilterFn: () => null,
          valueGetter: (_, row) =>
            FaroCoreApiUtils.Member.isMemberActive(row.entity)
              ? UtilsTypes.MemberStatus.active
              : UtilsTypes.MemberStatus.pending,
          renderCell: (params) =>
            MemberStatusCell(params, "workspace-members-table"),
        },

        authenticationType: {
          field: "authenticationType",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.authenticationType,
          ...columnDimension.authenticationType,
          getApplyQuickFilterFn: () => null,
          valueGetter: (_, row) => row.entity.authTypeInfo.name,
          renderCell: MemberAuthenticationTypeCell,
        },

        options: {
          field: "options",
          type: "actions",
          headerName: MEMBERS_TABLE_COLUMN_LABELS.options,
          ...columnDimension.options,
          getApplyQuickFilterFn: () => null,
          align: "right",
          renderCell: (params) =>
            MemberOptionCell({
              params,
              company,
              location: "workspace-members-table",
            }),
        },
      };
    }, [company]);

  return Object.values(columnsMap);
}
